import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import SEOMeta from "@/components/SEOMeta";
import ExitIntent from "@/components/ExitIntent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, Calendar, Tag, Mail, ChevronLeft, ExternalLink, Bell } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  "market-update": "Market Update",
  tutorial: "Tutorial",
  feature: "Feature",
  announcement: "Announcement",
};

const CATEGORY_COLORS = {
  "market-update": "text-emerald-400 border-emerald-500/30 bg-emerald-500/8",
  tutorial:        "text-blue-400 border-blue-500/30 bg-blue-500/8",
  feature:         "text-purple-400 border-purple-500/30 bg-purple-500/8",
  announcement:    "text-amber-400 border-amber-500/30 bg-amber-500/8",
};

// Gradient thumbnail per category (no external images needed)
const CATEGORY_GRADIENT = {
  "market-update": "from-emerald-900/40 via-emerald-950/20 to-transparent",
  tutorial:        "from-blue-900/40 via-blue-950/20 to-transparent",
  feature:         "from-purple-900/40 via-purple-950/20 to-transparent",
  announcement:    "from-amber-900/40 via-amber-950/20 to-transparent",
};

const CATEGORY_ICON_CHAR = {
  "market-update": "📊",
  tutorial:        "📖",
  feature:         "✦",
  announcement:    "📣",
};

function CategoryBadge({ category }) {
  const cls = CATEGORY_COLORS[category] || "text-white/50 border-white/10 bg-white/5";
  return (
    <span className={`text-[10px] font-section tracking-wider uppercase px-2 py-0.5 border rounded-sm ${cls}`}>
      {CATEGORY_LABELS[category] || category}
    </span>
  );
}

function MarkdownBody({ body }) {
  // Convert markdown headers, bold, code blocks, bullet lists, paragraphs to HTML
  let html = body
    .replace(/^## (.+)$/gm, '<h2 class="text-base font-section mt-6 mb-2 text-white">$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="text-sm font-section mt-4 mb-1 txt-secondary">$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em class='txt-secondary'>$1</em>")
    .replace(/`(.*?)`/g, "<code class='bg-white/10 px-1 rounded text-xs font-mono'>$1</code>")
    .replace(/^- (.+)$/gm, '<li class="ml-4 list-disc txt-secondary">$1</li>')
    .replace(/\n\n/g, '</p><p class="txt-secondary text-sm leading-relaxed mb-3">');
  html = `<p class="txt-secondary text-sm leading-relaxed mb-3">${html}</p>`;
  return <div className="prose-sm" dangerouslySetInnerHTML={{ __html: html }} />;
}

function PostCard({ post, onClick }) {
  const gradient = CATEGORY_GRADIENT[post.category] || "from-zinc-900/40 via-zinc-950/20 to-transparent";
  const iconChar = CATEGORY_ICON_CHAR[post.category] || "◆";

  return (
    <div
      onClick={() => onClick(post)}
      className="panel panel-hover cursor-pointer group overflow-hidden flex flex-col"
    >
      {/* Thumbnail */}
      <div className={`h-[80px] bg-gradient-to-br ${gradient} border-b border-[var(--border)] relative flex items-center justify-center`}>
        <span className="text-3xl opacity-20 select-none">{iconChar}</span>
        {post.auto_generated && (
          <span className="absolute top-2 right-2 text-[9px] font-section tracking-widest uppercase text-[var(--text-muted)] bg-[var(--bg-surface-2)] border border-[var(--border)] px-1.5 py-0.5">
            AI
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <div className="mb-2">
          <CategoryBadge category={post.category} />
        </div>
        <h3 className="font-section text-[13px] text-[var(--text-primary)] mb-1.5 group-hover:text-[var(--accent)] transition-colors line-clamp-2 leading-snug">
          {post.title}
        </h3>
        <p className="text-[var(--text-muted)] text-[11px] leading-relaxed mb-3 line-clamp-2 flex-1">{post.summary}</p>
        <div className="flex items-center gap-3 text-[var(--text-muted)] text-[10px] font-mono-data mt-auto pt-2 border-t border-[var(--border)]">
          <span className="flex items-center gap-1">
            <Calendar className="w-2.5 h-2.5" />
            {post.published_at?.slice(0, 10)}
          </span>
          {/* Only show views if > 0 to avoid "0 views" empty state */}
          {(post.views || 0) > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="w-2.5 h-2.5" />{post.views}
            </span>
          )}
          {post.tags?.slice(0, 1).map((t) => (
            <span key={t} className="ml-auto text-[var(--text-muted)]">#{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function NewsletterWidget() {
  const [email, setEmail] = useState("");
  const [freq, setFreq] = useState("weekly");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/blog/subscribe", { email, frequency: freq });
      setDone(true);
      toast.success("Subscribed! Market updates coming your way.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="panel p-5 border-[var(--accent)]/20 text-center">
        <div className="txt-accent font-section text-sm mb-1">Subscribed!</div>
        <div className="txt-muted text-xs">You'll receive {freq} market updates.</div>
      </div>
    );
  }

  return (
    <div className="panel p-5">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 txt-accent" />
        <span className="overline">Newsletter</span>
      </div>
      <p className="txt-secondary text-xs mb-4">Get AI-curated market intelligence delivered to your inbox.</p>
      <div className="flex gap-2 mb-3">
        {["daily", "weekly", "monthly"].map((f) => (
          <button
            key={f}
            onClick={() => setFreq(f)}
            className={`text-xs font-section tracking-wider px-2.5 py-1 border transition-colors ${
              freq === f ? "border-white text-white bg-white/10" : "border-[var(--border)] txt-muted"
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          type="email"
          className="rounded-none bg-[var(--bg-surface)] border-[var(--border)] text-sm h-8 flex-1"
          onKeyDown={(e) => e.key === "Enter" && subscribe()}
        />
        <Button size="sm" onClick={subscribe} disabled={loading || !email} className="h-8 rounded-none">
          <Mail className="w-3.5 h-3.5 mr-1" />
          {loading ? "…" : "Subscribe"}
        </Button>
      </div>
    </div>
  );
}


export default function Blog() {
  const { user } = useAuth();
  const { slug: urlSlug } = useParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const [activePost, setActivePost] = useState(null);
  const [postLoading, setPostLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = (p = 1, cat = category) => {
    setLoading(true);
    const params = { page: p, page_size: 12, ...(cat ? { category: cat } : {}) };
    api.get("/blog/posts", { params }).then(({ data }) => {
      setPosts(data.posts || []);
      setTotal(data.total || 0);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  // Auto-open post if URL slug provided
  useEffect(() => {
    if (urlSlug) {
      api.get(`/blog/posts/${urlSlug}`).then(({ data }) => setActivePost(data)).catch(() => {});
    }
  }, [urlSlug]);

  useEffect(() => { load(); }, []);

  const openPost = async (post) => {
    setPostLoading(true);
    try {
      const { data } = await api.get(`/blog/posts/${post.slug}`);
      setActivePost(data);
    } catch {
      toast.error("Could not load post");
    } finally {
      setPostLoading(false);
    }
  };

  const Wrapper = user ? AppShell : ({ children }) => <div className="min-h-screen bg-[var(--bg-page)] text-white">{!user && <PublicNav />}{children}</div>;

  if (activePost) {
    return (
      <Wrapper>
        <SEOMeta title={activePost.title} description={activePost.summary} url={`/blog/${activePost.slug}`} type="article" publishedAt={activePost.published_at} author={activePost.author} />
      <ExitIntent />
      <div className="p-4 sm:p-6 lg:p-8 max-w-3xl">
          <button
            onClick={() => setActivePost(null)}
            className="flex items-center gap-1.5 txt-muted text-xs font-section tracking-wider hover:text-white transition-colors mb-6"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back to Blog
          </button>
          <div className="flex items-center gap-2 mb-3">
            <CategoryBadge category={activePost.category} />
            {activePost.auto_generated && (
              <span className="text-[10px] font-section text-white/30 tracking-wider">AI GENERATED</span>
            )}
          </div>
          <h1 className="font-section text-xl text-white mb-2">{activePost.title}</h1>
          <div className="flex items-center gap-4 txt-muted text-xs mb-6">
            <span>{activePost.published_at?.slice(0, 10)}</span>
            <span>By {activePost.author}</span>
            <span><Eye className="w-3 h-3 inline mr-0.5" />{activePost.views}</span>
          </div>
          <div className="panel p-5 mb-6">
            {postLoading ? (
              <div className="txt-muted text-sm text-center py-4">Loading…</div>
            ) : (
              <MarkdownBody body={activePost.body || ""} />
            )}
          </div>
          {activePost.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {activePost.tags.map((t) => (
                <span key={t} className="text-xs txt-muted border border-[var(--border)] px-2 py-0.5">#{t}</span>
              ))}
            </div>
          )}
          <NewsletterWidget />
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <SEOMeta title="Market Blog — Daily Nifty Intelligence" description="AI-generated daily market updates for Indian traders. Nifty options sentiment, PCR, IV Rank, key levels — published every morning at 8am IST." url="/blog" />
      <ExitIntent />
      {user && <PageHeader
        overline="Intelligence"
        title="Market Blog"
        description="Daily AI-generated market updates, tutorials, and platform news."
      />}
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-6xl">

        {/* Category filter */}
        <div className="flex flex-wrap gap-1.5">
          {["", "market-update", "tutorial", "feature", "announcement"].map((cat) => (
            <button
              key={cat || "all"}
              onClick={() => { setCategory(cat); setPage(1); load(1, cat); }}
              className={`text-[11px] font-section tracking-wider px-3 py-1.5 transition-colors ${
                category === cat
                  ? "bg-[var(--bg-surface-2)] text-[var(--text-primary)] border border-[var(--border-strong)]"
                  : "border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:border-[var(--border-strong)]"
              }`}
            >
              {cat ? CATEGORY_LABELS[cat] : "All Posts"}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="panel h-[200px] animate-pulse opacity-50" />
            ))
          ) : posts.length === 0 ? (
            <div className="col-span-3 text-center py-16">
              <div className="text-2xl mb-3 opacity-20">📰</div>
              <div className="text-[var(--text-muted)] text-sm font-section">No posts yet. Check back after market open.</div>
            </div>
          ) : (
            posts.map((post) => <PostCard key={post.id} post={post} onClick={openPost} />)
          )}
        </div>

        {/* Pagination */}
        {total > 12 && (
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} className="rounded-none">Prev</Button>
            <span className="text-[var(--text-muted)] text-xs font-mono-data">{page} / {Math.ceil(total / 12)}</span>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 12)} onClick={() => { setPage(page + 1); load(page + 1); }} className="rounded-none">Next</Button>
          </div>
        )}

        {/* Newsletter — moved to bottom, full-width banner style */}
        <div className="border-t border-[var(--border)] pt-8">
          <NewsletterWidget />
        </div>
      </div>
    </Wrapper>
  );
}
